import React from "react";
import { useTranslation } from "react-i18next";
import { Grid2, Typography } from "@mui/material";
import { GainLossPercents } from "@rentablo/components";
import { useMandatorContext } from "@rentablo/theming";
import { toPercent, toRoundedNumber } from "@helpers";
import ChartSquareIcon from "@icons/myicons/chart-square-1.svg?react";
import { InfoAccordion } from "../components";
import { AccordionSummaryHeader } from "./AccordionSummaryHeader";
import type { InvestmentPerformance } from "@rentablo/api/apiv1";
import type { GainLossValues } from "../helpers";

interface Props {
  values: GainLossValues;
  investmentPerformance: InvestmentPerformance;
  isForeignCurrencyInvestment: boolean;
}

export const PerformanceAccordion = ({
  values,
  investmentPerformance,
  isForeignCurrencyInvestment,
}: Props) => {
  const { isKpmg } = useMandatorContext();
  const { t } = useTranslation();
  const labelColor = isKpmg ? "textPrimary" : "textSecondary";
  const performanceLabel = values.showIrr
    ? t("investment.details.gainLossStatistics.irr", {
        years: toRoundedNumber(
          investmentPerformance.holdingTimeInYears || 0,
          1
        ),
      })
    : t("investment.details.gainLossStatistics.twror");
  const performanceValue = values.showIrr
    ? (values.performance?.irrWithDividends ?? 0)
    : (values.performance?.twrorWithDividends ?? 0);

  return (
    <InfoAccordion
      id="performance"
      expandIconTestId="expand-performance"
      summary={
        <AccordionSummaryHeader
          testId="gainLossStatistics.performanceOrIrrWithDividends"
          icon={<ChartSquareIcon width={20} height={20} />}
          label={performanceLabel}
          primaryValue={<GainLossPercents value={performanceValue} />}
        />
      }
    >
      <Grid2 container rowSpacing={2} columnSpacing={2} mt={3}>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {isForeignCurrencyInvestment
              ? t(
                  "investment.details.gainLossStatistics.fromMarketAndCurrencyReturns"
                )
              : t("investment.details.gainLossStatistics.fromMarketReturns")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.performanceOrIrr"
          >
            {toPercent(
              values.showIrr
                ? values.performance?.irr
                : values.performance?.twror
            )}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 7 }}>
          <Typography color={labelColor} variant="body3">
            {t("investment.details.gainLossStatistics.fromDividendsAndKupons")}
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 5 }}>
          <Typography
            color="textPrimary"
            variant="body4"
            data-testid="gainLossStatistics.performanceOrIrrFromDividends"
          >
            {toPercent(
              values.showIrr
                ? (values.performance?.irrWithDividends || 0) -
                    (values.performance?.irr || 0)
                : (values.performance?.twrorWithDividends || 0) -
                    (values.performance?.twror || 0)
            )}
          </Typography>
        </Grid2>
      </Grid2>
    </InfoAccordion>
  );
};
